import { FlexBox } from '@fable/components'
import { css, cx, useTheme } from '@fable/theme'
import { ProgressCircle } from './shared'
import { motion } from 'framer-motion'
import { CompProps } from 'types'
import { useStats } from '../lib/ctx/stats'

export const StatsLoader = ({
  className = '',
  color,
  bgColor,
}: {
  className?: string
  color?: string
  bgColor?: string
}) => {
  const isProfileView = useStats((s) => s.isProfileView)
  const { colors, mediaQueries } = useTheme()
  const blockBgColor = color || colors.uiPearl

  const getBgColor = () => {
    if (bgColor) return bgColor

    return isProfileView ? colors.uiWhite : colors.uiPearlExtraLight
  }

  const readerSummaryLoader = (
    <>
      <MotionBlock
        className={css`
          display: flex;
          justify-content: center;
          align-items: center;
          position: absolute;
          top: 39px;
          left: 50%;
          transform: translateX(-50%);
        `}
        noBg
      >
        <ProgressCircle
          percentage={0}
          width={166}
          backgroundColor={blockBgColor}
          cutout
        />
      </MotionBlock>
      <MotionBlock
        className={css`
          display: block;
        `}
        width="92px"
        height="92px"
        borderRadius="50%"
        bgColor={color || ''}
      />
      <MotionBlock
        delay={0.25}
        className={css`
          margin-top: 20px;
          gap: 8px;
          display: flex;
          align-items: center;
          flex-direction: column;
        `}
        noBg
      >
        <span
          className={css`
            width: 118px;
            height: 18px;
            background-color: ${blockBgColor};
            border-radius: 50px;
          `}
        />
        <span
          className={css`
            width: 160px;
            height: 40px;
            background-color: ${blockBgColor};
            border-radius: 150px;
          `}
        />
        <span
          className={css`
            width: 118px;
            height: 18px;
            background-color: ${blockBgColor};
            border-radius: 50px;
          `}
        />
      </MotionBlock>
    </>
  )

  const profileLoader = (
    <MotionBlock height="250px" delay={0.25} bgColor={color || ''} />
  )

  return (
    <div
      className={cx(
        css`
          width: 100%;
          height: 100dvh;
          overflow: hidden;
          background-color: ${getBgColor()};
        `,
        className
      )}
    >
      <FlexBox
        className={css`
          padding: 0 20px;
          padding-top: ${isProfileView ? 0 : 76}px;
          width: 100%;
          position: relative;
          ${mediaQueries.tablet} {
            padding-top: ${isProfileView ? 20 : 76}px;
          }
        `}
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
      >
        {isProfileView ? profileLoader : readerSummaryLoader}
        <FlexBox
          className={css`
            margin-top: 20px;
            gap: 20px;
            width: 100%;
          `}
          flexDirection="column"
        >
          <MotionBlock height="332px" delay={0.5} bgColor={color || ''} />
          <MotionBlock height="332px" delay={0.75} bgColor={color || ''} />
        </FlexBox>
      </FlexBox>
    </div>
  )
}

export const MotionBlock = ({
  delay = 0,
  children,
  className = '',
  width = '100%',
  height = 'auto',
  borderRadius = '20px',
  noBg = false,
  bgColor,
}: CompProps<{
  delay?: number
  className?: string
  width?: string
  height?: string
  borderRadius?: string
  noBg?: boolean
  bgColor?: string
}>) => {
  const isProfileView = useStats((s) => s.isProfileView)
  const { colors } = useTheme()

  const getBgColor = () => {
    if (bgColor) return bgColor
    if (noBg) return 'transparent'
    if (isProfileView) return colors.uiPearlExtraLight
    return colors.uiPearl
  }

  return (
    <motion.div
      initial={{ opacity: 0.25 }}
      animate={{ opacity: 1 }}
      transition={{
        repeat: Infinity,
        repeatType: 'reverse',
        duration: 0.8,
        delay,
      }}
      className={cx(
        css`
          width: ${width};
          height: ${height};
          border-radius: ${borderRadius};
          background-color: ${getBgColor()};
        `,
        className
      )}
    >
      {children}
    </motion.div>
  )
}
